import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, Star, MapPin, Phone, Globe, Clock, ChevronLeft, ChevronRight,
  ExternalLink, Heart, Share2, Navigation, Mail, MessageCircle
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Price level display
const PRICE_LEVELS = {
  1: { label: 'Budget', symbol: '$' },
  2: { label: 'Moderate', symbol: '$$' },
  3: { label: 'Upscale', symbol: '$$$' },
  4: { label: 'Luxury', symbol: '$$$$' }
};

// Day names for hours display
const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export default function PlaceDetails({ place, onClose }) {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activePhotoIndex, setActivePhotoIndex] = useState(0);
  const [isFavorite, setIsFavorite] = useState(false);

  const fetchDetails = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/places/details/${place.fsq_id}`);
      setDetails(response.data);
    } catch (err) {
      // Use basic place data if details fetch fails
      setDetails(place);
      console.error('Failed to fetch place details:', err);
    } finally {
      setLoading(false);
    }
  }, [place]);

  useEffect(() => {
    if (place?.fsq_id) {
      fetchDetails();
    } else if (place) {
      // Use provided place data as details
      setDetails(place);
      setLoading(false);
    }
  }, [place, fetchDetails]);

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: details?.name,
          text: `Check out ${details?.name} on AIRA ECHO`,
          url: window.location.href
        });
      } catch (err) {
        console.error('Share failed:', err);
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success('Link copied to clipboard!');
    }
  };

  const openInMaps = () => {
    if (details?.latitude && details?.longitude) {
      const url = `https://www.google.com/maps/search/?api=1&query=${details.latitude},${details.longitude}`;
      window.open(url, '_blank');
    }
  };

  const nextPhoto = () => {
    if (details?.photos?.length > 0) {
      setActivePhotoIndex((prev) => (prev + 1) % details.photos.length);
    }
  };

  const prevPhoto = () => {
    if (details?.photos?.length > 0) {
      setActivePhotoIndex((prev) => (prev - 1 + details.photos.length) % details.photos.length);
    }
  };

  if (!place) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-[var(--bg-primary)] rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {loading ? (
            <div className="h-96 flex items-center justify-center">
              <div className="animate-spin w-8 h-8 border-2 border-[var(--color-primary)] border-t-transparent rounded-full" />
            </div>
          ) : details ? (
            <>
              {/* Photo Gallery */}
              <div className="relative h-64 md:h-80">
                <img
                  src={
                    details.photos?.[activePhotoIndex]?.url ||
                    details.photos?.[activePhotoIndex]?.url_medium ||
                    details.image ||
                    'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=800&q=80'
                  }
                  alt={details.name}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/30" />

                {/* Photo Navigation */}
                {details.photos?.length > 1 && (
                  <>
                    <button
                      onClick={prevPhoto}
                      className="absolute left-4 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/50 hover:bg-black/70 text-white transition-colors"
                    >
                      <ChevronLeft className="w-6 h-6" />
                    </button>
                    <button
                      onClick={nextPhoto}
                      className="absolute right-4 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/50 hover:bg-black/70 text-white transition-colors"
                    >
                      <ChevronRight className="w-6 h-6" />
                    </button>
                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                      {details.photos.slice(0, 5).map((_, index) => (
                        <button
                          key={index}
                          onClick={() => setActivePhotoIndex(index)}
                          className={`w-2 h-2 rounded-full transition-colors ${
                            index === activePhotoIndex ? 'bg-white' : 'bg-white/50'
                          }`}
                        />
                      ))}
                    </div>
                  </>
                )}

                {/* Close Button */}
                <button
                  onClick={onClose}
                  className="absolute top-4 right-4 p-2 rounded-full bg-black/50 hover:bg-black/70 text-white transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>

                {/* Action Buttons */}
                <div className="absolute top-4 left-4 flex gap-2">
                  <button
                    onClick={() => setIsFavorite(!isFavorite)}
                    className={`p-2 rounded-full transition-colors ${
                      isFavorite 
                        ? 'bg-red-500 text-white' 
                        : 'bg-black/50 hover:bg-black/70 text-white'
                    }`}
                  >
                    <Heart className={`w-5 h-5 ${isFavorite ? 'fill-current' : ''}`} />
                  </button>
                  <button
                    onClick={handleShare}
                    className="p-2 rounded-full bg-black/50 hover:bg-black/70 text-white transition-colors"
                  >
                    <Share2 className="w-5 h-5" />
                  </button>
                </div>

                {/* Rating & Price Badge */}
                <div className="absolute bottom-4 right-4 flex gap-2">
                  {details.rating && (
                    <div className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-black/50 backdrop-blur-sm">
                      <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                      <span className="text-white font-medium">
                        {typeof details.rating === 'number' ? details.rating.toFixed(1) : details.rating}
                      </span>
                    </div>
                  )}
                  {details.price && (
                    <div className="px-3 py-1.5 rounded-full bg-[var(--color-gold)]/80 backdrop-blur-sm text-black font-medium">
                      {PRICE_LEVELS[details.price]?.symbol || details.price}
                    </div>
                  )}
                </div>
              </div>

              {/* Content */}
              <div className="p-6 max-h-[50vh] overflow-y-auto">
                {/* Header */}
                <div className="mb-6">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h2 className="text-2xl font-bold text-[var(--text-primary)]">{details.name}</h2>
                      <p className="text-[var(--color-primary)] font-medium mt-1">
                        {details.category || details.categories?.join(', ')}
                      </p>
                    </div>
                  </div>
                  
                  {/* Address */}
                  <div className="flex items-start gap-2 mt-3 text-[var(--text-secondary)]">
                    <MapPin className="w-5 h-5 mt-0.5 flex-shrink-0" />
                    <p>{details.address || `${details.locality || ''}, ${details.country || ''}`}</p>
                  </div>
                </div>

                {/* Description */}
                {details.description && (
                  <div className="mb-6">
                    <h3 className="text-sm font-semibold text-[var(--text-secondary)] uppercase tracking-wider mb-2">
                      About
                    </h3>
                    <p className="text-[var(--text-primary)]">{details.description}</p>
                  </div>
                )}

                {/* Hours */}
                {details.hours && (
                  <div className="mb-6">
                    <h3 className="text-sm font-semibold text-[var(--text-secondary)] uppercase tracking-wider mb-2">
                      Hours
                    </h3>
                    <div className="flex items-center gap-2 mb-2">
                      <Clock className="w-4 h-4 text-[var(--text-secondary)]" />
                      <span className={`font-medium ${
                        details.hours.is_open ? 'text-green-500' : 'text-red-500'
                      }`}>
                        {details.hours.is_open ? 'Open Now' : 'Closed'}
                      </span>
                    </div>
                    {details.hours.display && (
                      <p className="text-[var(--text-secondary)] text-sm">{details.hours.display}</p>
                    )}
                  </div>
                )}

                {/* Contact & Links */}
                <div className="flex flex-wrap gap-3 mb-6">
                  {details.phone && (
                    <a
                      href={`tel:${details.phone}`}
                      className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[var(--bg-card)] border border-[var(--border-primary)] text-[var(--text-primary)] hover:border-[var(--color-primary)] transition-colors"
                    >
                      <Phone className="w-4 h-4" />
                      <span className="text-sm">Call</span>
                    </a>
                  )}
                  {details.website && (
                    <a
                      href={details.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[var(--bg-card)] border border-[var(--border-primary)] text-[var(--text-primary)] hover:border-[var(--color-primary)] transition-colors"
                    >
                      <Globe className="w-4 h-4" />
                      <span className="text-sm">Website</span>
                    </a>
                  )}
                  <button
                    onClick={openInMaps}
                    className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[var(--bg-card)] border border-[var(--border-primary)] text-[var(--text-primary)] hover:border-[var(--color-primary)] transition-colors"
                  >
                    <Navigation className="w-4 h-4" />
                    <span className="text-sm">Directions</span>
                  </button>
                </div>

                {/* Tips/Reviews */}
                {details.tips?.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-sm font-semibold text-[var(--text-secondary)] uppercase tracking-wider mb-3">
                      Reviews
                    </h3>
                    <div className="space-y-3">
                      {details.tips.map((tip, index) => (
                        <div key={index} className="p-4 rounded-xl bg-[var(--bg-card)] border border-[var(--border-primary)]">
                          <p className="text-[var(--text-primary)] text-sm">{tip.text}</p>
                          {tip.agree_count > 0 && (
                            <p className="text-xs text-[var(--text-secondary)] mt-2">
                              {tip.agree_count} people agree
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Features/Amenities */}
                {details.features && Object.keys(details.features).length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-sm font-semibold text-[var(--text-secondary)] uppercase tracking-wider mb-3">
                      Amenities
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(details.features).map(([key, value]) => (
                        value && (
                          <span key={key} className="px-3 py-1 rounded-full bg-[var(--bg-secondary)] text-[var(--text-secondary)] text-sm">
                            {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </span>
                        )
                      ))}
                    </div>
                  </div>
                )}

                {/* Tastes */}
                {details.tastes?.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-[var(--text-secondary)] uppercase tracking-wider mb-3">
                      Known For
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {details.tastes.map((taste, index) => (
                        <span key={index} className="px-3 py-1 rounded-full bg-[var(--color-primary)]/10 text-[var(--color-primary)] text-sm">
                          {taste}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="h-96 flex items-center justify-center text-[var(--text-secondary)]">
              No details available
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
